import {
  CheckSquareIcon,
  CogIcon,
  FolderIcon,
  LayoutDashboardIcon,
  MessageCircleQuestionIcon,
  UserLockIcon,
} from "lucide-react";
import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { MagnifyingGlassIcon } from "@heroicons/react/24/solid";
import { DealNavbar } from "../../nav";
import { TabConfig } from "../../nav/types";
import { DealRoleType } from "@/src/graphql/generated";
import { usePrefetchDataRoom } from "@/src/hooks/usePrefetchDataRoom";
import { KeyboardShortcut } from "../../keyboard_shortcut";
import { Search } from "../search";

export function DealNavigationTabs() {
  const { dealId: activeDealId } = useParams<{ dealId: string }>();
  const prefetchDataRoom = usePrefetchDataRoom(activeDealId ?? "");
  const [showSearch, setShowSearch] = useState(false);

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (
        event.target instanceof HTMLInputElement ||
        event.target instanceof HTMLTextAreaElement
      ) {
        return;
      }

      if (event.key === "/") {
        event.preventDefault();
        setShowSearch(true);
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, []);

  const tabs: TabConfig[] = [
    {
      label: "Dashboard",
      icon: <LayoutDashboardIcon className="w-4 h-4 text-inherit" />,
      to: `/deal/${activeDealId}/dashboard`,
    },
    {
      label: "Data Room",
      icon: <FolderIcon className="h-4 w-4 " />,
      to: `/deal/${activeDealId}/documents`,
      onMouseEnter: prefetchDataRoom.onMouseEnter,
      onMouseLeave: prefetchDataRoom.onMouseLeave,
      onFocus: prefetchDataRoom.onFocus,
    },
    {
      label: "Q&A",
      icon: <MessageCircleQuestionIcon className="w-4 h-4 " />,
      to: `/deal/${activeDealId}/questions`,
    },
    {
      label: "Access",
      icon: <UserLockIcon className="w-4 h-4 " />,
      to: `/deal/${activeDealId}/access`,
      permissionCheck: {
        permissionKey: "access:has_access",
        roleType: DealRoleType.Internal,
      },
    },
    {
      label: "Access",
      icon: <UserLockIcon className="w-4 h-4 " />,
      to: `/deal/${activeDealId}/access`,
      permissionCheck: {
        permissionKey: "guest_access:has_access",
        roleType: DealRoleType.Guest,
      },
    },
    {
      label: "Settings",
      icon: <CogIcon className="w-4 h-4 " />,
      to: `/deal/${activeDealId}/settings`,
      permissionCheck: {
        permissionKey: "deal:edit",
      },
    },
  ];

  return (
    <div className="flex items-center justify-between gap-x-4 p-1.5 pr-3">
      <DealNavbar tabs={tabs} />
      <div
        onClick={(e) => {
          e.stopPropagation();
          setShowSearch(true);
        }}
        className="h-8 hidden lg:flex cursor-text items-center min-w-64 shrink-0 border border-concrete-200 bg-white rounded-md pl-2.5 pr-3 text-gray-900 sm:text-sm hover:shadow-sm"
      >
        <MagnifyingGlassIcon className="pointer-events-none mr-2 h-4 w-4 text-persian-400" />
        <p className="text-gray-400 truncate">Search for anything...</p>
        <KeyboardShortcut shortcut="/" />
      </div>
      <Search open={showSearch} onClose={() => setShowSearch(false)} />
    </div>
  );
}
